import { createRemoteJWKSet, jwtVerify } from 'jose';

/**
 * Reassembles a chunked cookie given a chunk count and a lookup function.
 * Shared core used by both the client-side (document.cookie) and
 * server-side (parsed Cookie header) readers, which differ only in how
 * they look up an individual cookie value by name.
 */
const reassembleChunks = (count, getChunk) => {
  let assembled = '';
  for (let i = 0; i < count; i++) {
    const chunk = getChunk(i);
    if (!chunk) {
      return undefined;
    }
    assembled += chunk;
  }
  return assembled;
};

/**
 * Reads and reassembles a chunked cookie from document.cookie.
 * If {name}_count exists, reads {name}_0, {name}_1, ... and concatenates.
 * Otherwise falls back to reading the single {name} cookie.
 */
const getChunkedCookie = (cookieName) => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return undefined;
  };

  const countStr = getCookie(`${cookieName}_count`);

  if (!countStr) {
    return getCookie(cookieName);
  }

  const count = parseInt(countStr, 10);
  if (isNaN(count) || count <= 0) {
    return getCookie(cookieName);
  }

  const assembled = reassembleChunks(count, i => getCookie(`${cookieName}_${i}`));
  if (assembled === undefined) {
    console.error(`[auth] Missing cookie chunk: ${cookieName}`);
  }
  return assembled;
};

/**
 * Reads the id_token (chunked), falling back to CF_Authorization (legacy, also chunked).
 */
const getToken = () => {
  return getChunkedCookie('id_token') || getChunkedCookie('CF_Authorization');
};

const getLoginUrl = (origin, clientId) => `${origin}/af/sso/new/login?client_id=${clientId}&redirect_uri=${encodeURIComponent(origin + '/af/sso/new/authorize')}`;

const getLogoutUrl = (origin, clientId, postLogoutRedirectUri) =>
  `${origin}/af/sso/new/logout?client_id=${clientId}&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;

const getUser = async (token, origin) => {
  const jwks = createRemoteJWKSet(new URL(`${origin}/af/sso/.well-known/jwks.json`));

  return jwtVerify(token, jwks).then(d => d.payload).catch(() => null);
};

const isAuthenticated = async (token, origin) => {
  if (!token) {
    return false;
  }

  const user = await getUser(token, origin);

  return !!(user) && (user.roles.includes('admin') || user.roles.includes('collaborator'));
};

export { reassembleChunks, getChunkedCookie, getLoginUrl, getLogoutUrl, getUser, isAuthenticated, getToken };
